import { useId, useState } from "react";
import { extractErrorMessage } from "@/api/client";
import {
  Button,
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  SkeletonText,
  StatusBadge,
} from "@/design-system/components/ui";
import {
  minutesToClock,
  minutesToTimeInput,
  timeInputToMinutes,
  useCallHours,
  useSaveCallHours,
} from "./useWording";

/** The server's default when a user has saved none (M8). */
const DEFAULT_START = 13 * 60;
const DEFAULT_END = 17 * 60;

/**
 * My call hours — the window the `{{Availability}}` line offers calls inside,
 * in the user's OWN zone. The server stores minutes after local midnight and
 * converts to the buyer's zone when it renders a message; this card never does
 * zone arithmetic, it only names the zone the two times are in.
 */
export function CallHoursCard() {
  const q = useCallHours();
  const save = useSaveCallHours();
  const startId = useId();
  const endId = useId();

  // Seeded once from the server, then owned by the inputs. A refetch after the
  // save must not overwrite a time somebody is halfway through typing.
  const [times, setTimes] = useState<{ start: string; end: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  if (q.data && times === null) {
    setTimes({
      start: minutesToTimeInput(q.data.startMinutes),
      end: minutesToTimeInput(q.data.endMinutes),
    });
  }

  const onSave = () => {
    if (!times) return;
    const start = timeInputToMinutes(times.start);
    const end = timeInputToMinutes(times.end);
    setSaved(false);
    if (start === null || end === null) {
      setError("Enter a start and an end time.");
      return;
    }
    if (end <= start) {
      setError("The end time must be after the start time.");
      return;
    }
    setError(null);
    save.mutate(
      { startMinutes: start, endMinutes: end },
      {
        onSuccess: (data) => {
          setTimes({
            start: minutesToTimeInput(data.startMinutes),
            end: minutesToTimeInput(data.endMinutes),
          });
          setSaved(true);
        },
        onError: (e) => setError(extractErrorMessage(e)),
      },
    );
  };

  return (
    <Card data-testid="wording-call-hours">
      <CardHeader>
        <CardTitle className="text-lead">My call hours</CardTitle>
        <CardDescription>
          The availability line in your messages offers call windows inside these hours.
        </CardDescription>
      </CardHeader>

      {q.isLoading ? (
        <SkeletonText lines={2} />
      ) : q.isError ? (
        <div className="flex flex-wrap items-center gap-space-2" role="alert">
          <span className="text-small text-status-error">
            Couldn't load your call hours. {extractErrorMessage(q.error)}
          </span>
          <Button size="sm" variant="secondary" onClick={() => void q.refetch()}>
            Retry
          </Button>
        </div>
      ) : q.data && times ? (
        <div className="flex flex-col gap-space-3">
          <p className="text-small text-onyx" data-testid="wording-call-hours-zone">
            Times are in your time zone: <strong>{q.data.timeZoneLabel}</strong>
          </p>
          {q.data.isDefault && (
            <StatusBadge tone="muted" className="self-start" data-testid="wording-call-hours-default">
              Using the default, {minutesToClock(DEFAULT_START)} to {minutesToClock(DEFAULT_END)}
            </StatusBadge>
          )}
          <div className="flex flex-wrap items-end gap-space-3">
            <div className="flex flex-col gap-space-1">
              <Label htmlFor={startId}>From</Label>
              <Input
                id={startId}
                type="time"
                className="w-36"
                value={times.start}
                onChange={(e) => {
                  setTimes({ ...times, start: e.target.value });
                  setSaved(false);
                }}
              />
            </div>
            <div className="flex flex-col gap-space-1">
              <Label htmlFor={endId}>To</Label>
              <Input
                id={endId}
                type="time"
                className="w-36"
                value={times.end}
                onChange={(e) => {
                  setTimes({ ...times, end: e.target.value });
                  setSaved(false);
                }}
              />
            </div>
            <Button size="sm" onClick={onSave} loading={save.isPending}>
              Save call hours
            </Button>
          </div>
          {error && (
            <p role="alert" className="text-caption text-status-error">
              {error}
            </p>
          )}
          {saved && !error && (
            <p role="status" className="text-caption text-status-success">
              Call hours saved.
            </p>
          )}
        </div>
      ) : null}
    </Card>
  );
}
